document.documentElement.classList.remove('no-js');

const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

class ExxenDrawer {
  constructor() {
    this.backdrop = document.querySelector('[data-drawer-backdrop]');
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drawer-open]');
      if (opener) this.open(document.querySelector(opener.dataset.drawerOpen), opener);
      if (event.target.closest('[data-drawer-close]') || event.target === this.backdrop) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (!this.active) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trap(event);
    });
  }
  open(drawer, opener) {
    if (!drawer) return;
    if (this.active) this.close(false);
    this.active = drawer; this.opener = opener;
    drawer.hidden = false; this.backdrop.hidden = false; opener.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => { drawer.dataset.open = 'true'; this.backdrop.dataset.open = 'true'; });
    document.body.classList.add('scroll-locked');
    drawer.querySelector(focusableSelector)?.focus();
  }
  trap(event) {
    const nodes = [...this.active.querySelectorAll(focusableSelector)];
    if (!nodes.length) return;
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  close(restore = true) {
    if (!this.active) return;
    const drawer = this.active; const opener = this.opener;
    drawer.dataset.open = 'false'; this.backdrop.dataset.open = 'false'; opener?.setAttribute('aria-expanded', 'false');
    setTimeout(() => { drawer.hidden = true; if (!this.active) this.backdrop.hidden = true; }, 220);
    document.body.classList.remove('scroll-locked'); this.active = null; this.opener = null;
    if (restore) opener?.focus();
  }
}

class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-mega-item]')];
    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', () => this.toggle(item));
      item.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') this.open(item); });
      item.addEventListener('pointerleave', (event) => { if (event.pointerType !== 'touch') this.close(item); });
      item.addEventListener('focusout', (event) => { if (!item.contains(event.relatedTarget)) this.close(item); });
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeAll(true); });
    document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.closeAll(); });
  }
  toggle(item) { item.dataset.open === 'true' ? this.close(item) : this.open(item); }
  open(item) { this.closeAll(false, item); item.dataset.open = 'true'; item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'true'); }
  close(item, restore = false) { item.dataset.open = 'false'; const trigger = item.querySelector('[data-mega-trigger]'); trigger?.setAttribute('aria-expanded', 'false'); if (restore) trigger?.focus(); }
  closeAll(restore = false, except = null) { this.items.forEach((item) => { if (item !== except && item.dataset.open === 'true') this.close(item, restore); }); }
}

class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')]; this.index = 0;
    if (this.slides.length < 2) return;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelectorAll('[data-dot]').forEach((dot, i) => dot.addEventListener('click', () => this.show(i)));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.show(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.show(this.index - 1); }
    });
    // Horizontal swipe; vertical scrolling stays native (touch-action: pan-y in CSS).
    this.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') this.swipeStart = { x: event.clientX, y: event.clientY }; });
    this.addEventListener('pointerup', (event) => {
      if (!this.swipeStart) return;
      const dx = event.clientX - this.swipeStart.x; const dy = event.clientY - this.swipeStart.y; this.swipeStart = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
    });
    // Start automatic rotation only after real engagement. Passive rotations
    // replace the page's largest image while it is still being measured and
    // force an unnecessary second hero download on non-interacting visits.
    if (this.dataset.autoplay === 'true' && !this.reducedMotion) {
      const engage = () => this.start();
      addEventListener('pointerdown', engage, { once: true, passive: true });
      addEventListener('keydown', engage, { once: true });
      addEventListener('scroll', engage, { once: true, passive: true });
    }
    this.addEventListener('pointerenter', () => this.stop()); this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop()); this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) this.start(); });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
  }
  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; slide.setAttribute('aria-hidden', String(i !== this.index)); });
    this.querySelectorAll('[data-dot]').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    const status = this.querySelector('[data-slide-status]'); if (status) status.textContent = `${this.index + 1} / ${this.slides.length}`;
  }
  start() { if (this.dataset.autoplay !== 'true' || this.reducedMotion || this.timer || this.slides.length < 2) return; this.timer = setInterval(() => this.show(this.index + 1), Number(this.dataset.delay) || 6000); }
  stop() { clearInterval(this.timer); this.timer = null; }
}

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-announcement]')]; this.index = 0;
    if (this.items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const tick = () => { this.items[this.index].hidden = true; this.index = (this.index + 1) % this.items.length; this.items[this.index].hidden = false; };
    this.timer = setInterval(tick, Number(this.dataset.delay) || 5000);
    this.addEventListener('pointerenter', () => { clearInterval(this.timer); this.timer = null; });
    this.addEventListener('pointerleave', () => { if (!this.timer) this.timer = setInterval(tick, Number(this.dataset.delay) || 5000); });
  }
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]'); this.panel = this.querySelector('[data-results]'); this.timer = null; this.controller = null;
    this.input?.addEventListener('input', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 180); });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });
  }
  async search() {
    const query = this.input.value.trim(); if (query.length < 2) { this.close(); return; }
    this.controller?.abort(); this.controller = new AbortController(); this.open(this.dataset.loadingText);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6&section_id=predictive-search`, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Search response failed');
      const html = await response.text(); this.panel.innerHTML = html.trim() || `<p>${this.dataset.emptyText}</p>`; this.open();
    } catch (error) { if (error.name !== 'AbortError') this.panel.innerHTML = `<p>${this.dataset.emptyText}</p>`; }
  }
  open(message) { if (message) this.panel.textContent = message; this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
  close() { this.panel.hidden = true; this.input?.setAttribute('aria-expanded', 'false'); }
  onKeydown(event) {
    if (event.key === 'Escape') { this.close(); this.input.focus(); return; }
    const options = [...this.panel.querySelectorAll('[role="option"]')]; if (!options.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault(); const current = options.indexOf(document.activeElement); const step = event.key === 'ArrowDown' ? 1 : -1; options[(current + step + options.length) % options.length].focus();
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() { this.slides = [...this.querySelectorAll('[data-gallery-slide]')]; this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => thumb.addEventListener('click', () => this.show(Number(thumb.dataset.galleryThumb)))); }
  show(index) { this.slides.forEach((slide, i) => { slide.hidden = i !== index; }); this.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index))); this.slides[index]?.focus?.(); }
}

customElements.define('exxen-mega-menu', MegaMenu);
customElements.define('hero-slider', HeroSlider);
customElements.define('announcement-rotator', AnnouncementRotator);
customElements.define('predictive-search', PredictiveSearch);
customElements.define('product-gallery', ProductGallery);
new ExxenDrawer();
